import { useTranslation } from 'react-i18next'
import { Link } from 'react-router-dom'
import { BrandLogo } from '~/components/BrandLogo'
import SkipLink, { MAIN_ID } from '~/components/SkipLink'
import { useBranding } from '~/helpers/branding/branding-context'
import { cn } from '~/lib/utils'
import Footer from '~/pages/components/Footer'
import LearnerUserMenu from '~/pages/components/LearnerUserMenu'
import LearnerSearch from '~/pages/scorm/components/learner-search/LearnerSearch'
import LearnerNav from '~/pages/scorm/components/LearnerNav'

/** Une seule largeur pour l'en-tête et le contenu : leurs bords s'alignent (#307). */
const LEARNER_CONTAINER = 'mx-auto w-full max-w-6xl px-4 sm:px-6'

/**
 * Le cadre commun des écrans apprenant : en-tête, contenu, pied de page.
 *
 * Extrait de `MySessions` parce qu'il y a désormais plus d'un écran de ce côté —
 * l'accueil et le parcours — et qu'un cadre recopié dérive au premier ajout.
 *
 * Il apporte surtout le PIED DE PAGE, qui manquait. `Footer` n'était monté que
 * dans `BackOfficeLayout` : la mention « propulsé par Studeva » imposée au plan
 * gratuit (#155), que `docs/MARQUE_BLANCHE.md` promet, ne s'affichait donc
 * jamais à un apprenant — c'est-à-dire au seul public qui la lise vraiment. Le
 * composant est repris tel quel, sans recopier la règle : le gratuit est une
 * décision de facturation, elle n'a pas à exister en deux exemplaires.
 */
function LearnerFrame({ children, label }: { children: React.ReactNode, label: string }) {
  const { t } = useTranslation()
  const { appName } = useBranding()

  return (
    // Le cadre ne peint plus la marque (#307) : sur toute la page, elle
    // écrasait l'écran, en sombre surtout. Elle reste dans le logo, l'onglet
    // actif et les boutons ; les surfaces sont celles du thème.
    <div className="flex min-h-dvh flex-col bg-background text-foreground">
      <SkipLink />
      <header className="border-b border-border bg-card">
        <div className={cn(LEARNER_CONTAINER, 'flex h-16 items-center gap-2 sm:gap-4')}>
          {/* Le logo ramène à l'accueil apprenant, comme partout ailleurs sur le web. */}
          {/* Le nom du lien dit où il mène, pas seulement le logo (RGAA 6.1). */}
          <Link to="/my-sessions" aria-label={t('learner.logoLink', { appName })} className="min-w-0 shrink rounded-md focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ring">
            <BrandLogo className="h-10 w-auto max-w-40 sm:max-w-56" fallbackClassName="block truncate font-heading text-lg" />
          </Link>
          <LearnerNav />
          <div className="flex flex-1 items-center justify-end gap-1 sm:gap-3">
            <LearnerSearch />
            {/* Le centre d'aide vit dans le menu utilisateur, plus dans la barre :
                il y disputait la place du logo et de la recherche (#307). */}
            <LearnerUserMenu />
          </div>
        </div>
      </header>

      {/* Le titre visible salue l'apprenant ; le nom du repère reste ici, pour
          les lecteurs d'écran, qui ont besoin de savoir OÙ ils sont et pas
          seulement qu'on leur dit bonjour. */}
      <main id={MAIN_ID} tabIndex={-1} className={cn(LEARNER_CONTAINER, 'flex-1 py-6 focus:outline-none sm:py-8')} aria-label={label}>
        {children}
      </main>

      <Footer />
    </div>
  )
}

export default LearnerFrame
